/* R32 v4. UI only: Shopify's native forms and existing purchase-state controller own checkout, pricing and selling plans. */
(() => {
  'use strict';
  if (window.__mmR32UI) return;
  window.__mmR32UI = true;
  const pendingForms = new Map();
  function releaseForm(form) {
    clearTimeout(pendingForms.get(form));
    pendingForms.delete(form);
    form.removeAttribute('aria-busy');
  }
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || event.defaultPrevented) return;
    let action;
    try { action = new URL(form.action, location.origin); } catch (_) { return; }
    if (action.origin !== location.origin || !/\/cart(?:\/add)?\/?$/.test(action.pathname) || form.method.toLowerCase() !== 'post') return;
    if (pendingForms.has(form)) {
      event.preventDefault();
      return;
    }
    form.setAttribute('aria-busy', 'true');
    pendingForms.set(form, setTimeout(() => releaseForm(form), 10000));
    queueMicrotask(() => {
      if (event.defaultPrevented) releaseForm(form);
    });
  });
  window.addEventListener('pageshow', () => {
    [...pendingForms.keys()].forEach(releaseForm);
  });
  const galleries = new WeakMap();
  const locale = (document.documentElement.lang || 'en').toLowerCase();
  const isEs = locale === 'es' || locale.startsWith('es-');
  const labels = {
    enlarge: isEs ? 'Ampliar' : 'Enlarge',
    filterProducts: isEs ? 'Filtrar productos' : 'Filter products'
  };
  const findAll = (scope, selector) => [
    ...(scope.matches?.(selector) ? [scope] : []),
    ...scope.querySelectorAll(selector)
  ];
  function revealHash() {
    if (!location.hash) return;
    let target;
    try { target = document.getElementById(decodeURIComponent(location.hash.slice(1))); }
    catch (_) { return; }
    if (!target) return;
    for (let node = target; node; node = node.parentElement) {
      if (node.tagName === 'DETAILS') node.open = true;
    }
    requestAnimationFrame(() => target.scrollIntoView({block: 'start', behavior: 'auto'}));
  }
  function initialize(scope = document) {
    findAll(scope, '[data-r32-navigation]:not([data-ready])').forEach(nav => {
      nav.dataset.ready = 'true';
      const mq = matchMedia('(max-width:900px)');
      const rootSummary = nav.querySelector(':scope > summary');
      const syncState = () => {
        if (!rootSummary) return;
        rootSummary.setAttribute('aria-expanded', String(mq.matches ? nav.dataset.open === 'true' : true));
      };
      rootSummary?.addEventListener('click', event => {
        if (!mq.matches) return;
        event.preventDefault();
        nav.dataset.open = nav.dataset.open === 'true' ? 'false' : 'true';
        syncState();
      });
      mq.addEventListener?.('change', syncState);
      syncState();
      nav.addEventListener('click', event => {
        const summary = event.target.closest('.r32-menu > summary');
        if (!summary) return;
        nav.querySelectorAll('.r32-menu[open]').forEach(other => {
          if (other !== summary.parentElement) other.open = false;
        });
      });
    });
    findAll(scope, '[data-r32-sort]:not([data-ready])').forEach(select => {
      select.dataset.ready = 'true';
      select.addEventListener('change', () => select.form?.requestSubmit());
    });
    findAll(scope, '[data-r32-tabs]:not([data-ready])').forEach(root => {
      const bar = root.querySelector('[data-r32-tabbar]');
      const tabs = [...(bar?.querySelectorAll('button[aria-controls]') || [])];
      if (!bar || !tabs.length) return;
      root.dataset.ready = 'true'; bar.hidden = false; bar.setAttribute('role', 'tablist');
      const specs = root.querySelector('[data-r32-specs-shared]');
      const specsHome = specs?.parentElement;
      const specsTarget = root.querySelector('[data-r32-specs-target]');
      const productTop = root.closest('.r32-product-top');
      const media = productTop?.querySelector(':scope > .r32-product-media');
      const desktopTabs = matchMedia('(min-width:990px)');
      const placementAnchor = document.createComment('R32 product tabs original position');
      root.before(placementAnchor);
      const syncPlacement = () => {
        if (!root.isConnected || !placementAnchor.isConnected) return;
        if (desktopTabs.matches && media) {
          if (root.parentElement !== media) media.append(root);
        } else if (root.previousSibling !== placementAnchor) {
          placementAnchor.after(root);
        }
      };
      syncPlacement();
      desktopTabs.addEventListener?.('change', syncPlacement);
      const activate = (tab, focus = false) => {
        tabs.forEach(button => {
          const active = button === tab;
          button.setAttribute('aria-selected', String(active));
          button.tabIndex = active ? 0 : -1;
          const panel = document.getElementById(button.getAttribute('aria-controls'));
          if (panel && root.contains(panel)) {
            panel.hidden = !active;
            panel.setAttribute('role', 'tabpanel');
            if (button.id) panel.setAttribute('aria-labelledby', button.id);
          }
        });
        if (specs && specsTarget && specsHome) {
          const destination = tab.hasAttribute('data-r32-specifications') ? specsTarget : specsHome;
          if (specs.parentElement !== destination) destination.append(specs);
        }
        if (focus) tab.focus();
        window.dispatchEvent(new Event('resize'));
      };
      tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          else return;
          event.preventDefault(); activate(tabs[next], true);
        });
      });
      activate(tabs[0]);
    });
    findAll(scope, '[data-r32-gallery]:not([data-ready])').forEach(gallery => {
      const image = gallery.querySelector('[data-r32-main-image]');
      const opener = gallery.querySelector('[data-r32-zoom]');
      const dialog = gallery.querySelector('dialog');
      const large = dialog?.querySelector('img');
      if (!image || !opener || !dialog || !large) return;
      gallery.dataset.ready = 'true';
      const thumbs = [...gallery.querySelectorAll('[data-r32-thumb]')];
      let full = image.dataset.full || image.src;
      let current = Math.max(0, thumbs.findIndex(thumb => thumb.getAttribute('aria-current') === 'true'));
      const select = thumb => {
        if (!thumb?.dataset.src) return;
        current = thumbs.indexOf(thumb);
        image.srcset = thumb.dataset.srcset || '';
        image.src = thumb.dataset.src;
        image.alt = thumb.dataset.alt || '';
        const caption = gallery.querySelector('[data-r32-caption]');
        if (caption) {
          caption.textContent = thumb.dataset.caption || '';
          caption.hidden = caption.textContent.trim() === '';
        }
        full = thumb.dataset.full || thumb.dataset.src;
        opener.setAttribute('aria-label', labels.enlarge + ' ' + image.alt);
        thumbs.forEach(item => item.setAttribute('aria-current', String(item === thumb)));
      };
      galleries.set(gallery, {select, thumbs});
      thumbs.forEach(thumb => thumb.addEventListener('click', () => select(thumb)));
      gallery.querySelectorAll('[data-r32-gallery-step]').forEach(button => {
        button.disabled = thumbs.length < 2;
        button.addEventListener('click', () => {
          if (!thumbs.length) return;
          const step = Number(button.dataset.r32GalleryStep) || 1;
          select(thumbs[(current + step + thumbs.length) % thumbs.length]);
        });
      });
      opener.addEventListener('click', () => {
        large.src = full; large.alt = image.alt;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else window.open(full, '_blank', 'noopener');
      });
      dialog.querySelector('[data-r32-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
      dialog.addEventListener('close', () => {
        if (opener.isConnected) opener.focus({preventScroll:true});
      });
    });
    /* Variant facts update even on products with no image gallery. */
    findAll(scope, '[data-mm-product-root]:not([data-r32-variant-ready])').forEach(root => {
      const select = root.querySelector('[data-mm-variant-select]');
      if (!select) return;
      root.dataset.r32VariantReady = 'true';
      select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        if (!option) return;
        root.querySelectorAll('[data-mm-sku-row] [data-mm-sku]').forEach(node => node.textContent = option.dataset.mmSku || '');
        root.querySelectorAll('[data-mm-sku-row]').forEach(node => node.hidden = !option.dataset.mmSku);
        const controller = galleries.get(root.querySelector('[data-r32-gallery]'));
        const thumb = controller?.thumbs.find(item => item.dataset.imageId === option.dataset.mmImageId);
        if (thumb) controller.select(thumb);
        const qty = root.querySelector('input[name="quantity"]');
        if (qty) {
          qty.min = option.dataset.mmMin || '1';
          qty.step = option.dataset.mmStep || '1';
          qty.value = qty.min;
          if (option.dataset.mmMax) qty.max = option.dataset.mmMax;
          else qty.removeAttribute('max');
          qty.dispatchEvent(new Event('change', {bubbles: true}));
        }
      });
    });
    findAll(scope, '.r32-quantity:not([data-ready])').forEach(group => {
      const input = group.querySelector('input[name="quantity"]');
      if (!input) return;
      group.dataset.ready = 'true';
      const refresh = () => {
        const min = Number(input.min) || 1;
        const max = input.max ? Number(input.max) : Infinity;
        const value = Number(input.value) || min;
        group.querySelectorAll('[data-r32-quantity]').forEach(button => {
          button.disabled = Number(button.dataset.r32Quantity) < 0 ? value <= min : value >= max;
        });
      };
      group.querySelectorAll('[data-r32-quantity]').forEach(button => button.addEventListener('click', () => {
        try {
          if (!Number.isFinite(input.valueAsNumber)) input.value = input.min || '1';
          input.stepUp(Number(button.dataset.r32Quantity) < 0 ? -1 : 1);
        } catch (_) { return; }
        input.dispatchEvent(new Event('input', {bubbles: true}));
        input.dispatchEvent(new Event('change', {bubbles: true}));
      }));
      input.addEventListener('input', refresh); input.addEventListener('change', refresh); refresh();
    });
    findAll(scope, '[data-r32-catalog]:not([data-ready])').forEach(catalog => {
      catalog.dataset.ready = 'true';
      const grid = catalog.querySelector('[data-r32-product-grid]');
      catalog.querySelectorAll('[data-r32-view]').forEach(button => button.addEventListener('click', () => {
        if (!grid) return;
        grid.dataset.view = button.dataset.r32View;
        catalog.querySelectorAll('[data-r32-view]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      }));
      const filter = catalog.querySelector('[data-r32-filter]');
      const form = filter?.querySelector('form');
      const toggle = filter?.querySelector('.r32-filter-mobile-toggle');
      if (form && toggle) {
        const mq = matchMedia('(max-width:600px)');
        const setOpen = open => {
          filter.dataset.open = String(open);
          toggle.setAttribute('aria-expanded', String(open));
        };
        const sync = () => {
          if (!filter.isConnected) return;
          if (mq.matches) setOpen(false);
          else setOpen(true);
        };
        toggle.addEventListener('click', () => {
          const open = filter.dataset.open !== 'true';
          setOpen(open);
          if (open) form.querySelector('input,select,button,a[href]')?.focus({preventScroll:true});
        });
        form.addEventListener('keydown', event => {
          if (event.key !== 'Escape' || !mq.matches || filter.dataset.open !== 'true') return;
          event.preventDefault();
          setOpen(false);
          toggle.focus({preventScroll:true});
        });
        mq.addEventListener('change', sync);
        sync();
      }
    });
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview && /^\d+$/.test(preview)) findAll(scope, 'form[method="get"]').forEach(form => {
      if (form.querySelector('[name="preview_theme_id"]')) return;
      const field = document.createElement('input');
      field.type = 'hidden'; field.name = 'preview_theme_id'; field.value = preview; form.append(field);
    });
  }
  const menus = '.r32-menu[open], .r32-promo details[open], .r32-club-family[open]';
  document.addEventListener('click', event => {
    document.querySelectorAll(menus).forEach(menu => { if (!menu.contains(event.target)) menu.open = false; });
    const link = event.target.closest('a[href^="#"]');
    if (link && link.getAttribute('href') === location.hash) setTimeout(revealHash, 0);
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll(menus).forEach(menu => {
      const focused = menu.contains(document.activeElement);
      menu.open = false; if (focused) menu.querySelector('summary')?.focus();
    });
  });
  window.addEventListener('hashchange', revealHash);
  const ready = () => { initialize(); revealHash(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, {once: true});
  else ready();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();

